import React, { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import styled from 'styled-components';

import { IGroupZoomRecording } from 'core/hooks';
import { ZoomRecordingStatusEnum } from 'core/graphql';

import * as S from '../../FakturowniaInvoices/FakturowniaInvoices.styled';
import * as L from '../../_detailShared/detail.styled';
import { GroupColors } from './groupColors';

interface IZoomRecordingsListProps {
  recordings: IGroupZoomRecording[];
  loading: boolean;
}

const TopicName = styled.div`
  font-weight: 600;
  color: ${GroupColors.textPrimary};
`;

const MutedCell = styled.td`
  color: ${GroupColors.textSecondary};
`;

const fmtSize = (bytes: number | null | undefined): string => {
  if (!bytes || bytes < 0) return '—';
  const mb = bytes / 1024 / 1024;
  if (mb < 1024) return `${mb.toFixed(1)} MB`;
  return `${(mb / 1024).toFixed(2)} GB`;
};

/**
 * Status tone + i18n key for a recording. Now driven by the backend
 * `status` enum (B4): `FAILED` is a real danger state, distinct from `ORPHAN`
 * (not matched) and `TOO_SHORT`. The legacy boolean flags remain as fallbacks
 * for rows the backend has not (re)classified yet.
 */
const recordingTone = (
  rec: IGroupZoomRecording,
): { tone: 'ok' | 'warn' | 'danger' | 'neutral'; key: string } => {
  if (rec.deletedFromZoom) return { tone: 'neutral', key: 'deleted' };
  if (rec.status === ZoomRecordingStatusEnum.Failed) {
    return { tone: 'danger', key: 'failed' };
  }
  if (rec.isMatched) return { tone: 'ok', key: 'matched' };
  if (rec.isOrphan || rec.status === ZoomRecordingStatusEnum.Orphan) {
    return { tone: 'warn', key: 'orphan' };
  }
  if (rec.isTooShort || rec.status === ZoomRecordingStatusEnum.TooShort) {
    return { tone: 'neutral', key: 'tooShort' };
  }
  if (rec.status === ZoomRecordingStatusEnum.Processing) {
    return { tone: 'neutral', key: 'processing' };
  }
  return { tone: 'neutral', key: 'pending' };
};

/**
 * ZoomRecordingsList — backed by `allZoomRecordings(groupId)`
 * (backend TZ §G2). Newest-first table of recordings related to the
 * group, with status pill, duration, size and link out to the
 * recording detail page (`/zoom/recordings/:id`) for any action that
 * already lives there.
 */
export const ZoomRecordingsList = memo(
  ({ recordings, loading }: IZoomRecordingsListProps) => {
    const { t } = useTranslation();

    const rows = useMemo(
      () =>
        [...recordings].sort((a, b) =>
          (b.startTime || '').localeCompare(a.startTime || ''),
        ),
      [recordings],
    );

    if (rows.length === 0) {
      return (
        <S.Card>
          <S.CardTitle>
            {t('pages.groupDetail.zoom.recordingsTitle')}
          </S.CardTitle>
          <S.SubTitle>
            {loading
              ? t('pages.groupDetail.zoom.recordingsLoading')
              : t('pages.groupDetail.zoom.noRecordings')}
          </S.SubTitle>
        </S.Card>
      );
    }

    return (
      <S.Card>
        <S.CardTitle>{t('pages.groupDetail.zoom.recordingsTitle')}</S.CardTitle>
        <L.SmallTable>
          <thead>
            <tr>
              <th>{t('pages.groupDetail.zoom.col.topic')}</th>
              <th>{t('pages.groupDetail.zoom.col.startTime')}</th>
              <th>{t('pages.groupDetail.zoom.col.duration')}</th>
              <th>{t('pages.groupDetail.zoom.col.size')}</th>
              <th>{t('pages.groupDetail.zoom.col.status')}</th>
              <th>{t('pages.groupDetail.zoom.col.account')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((rec) => {
              const status = recordingTone(rec);
              return (
                <tr key={rec.id}>
                  <td>
                    <TopicName>{rec.topic || '—'}</TopicName>
                  </td>
                  <td>
                    {rec.startTime
                      ? moment(rec.startTime).format('DD.MM.YYYY HH:mm')
                      : '—'}
                  </td>
                  <MutedCell>
                    {rec.durationMinutes != null
                      ? `${rec.durationMinutes} ${t(
                          'pages.groupDetail.zoom.minutes',
                        )}`
                      : '—'}
                  </MutedCell>
                  <MutedCell>
                    {fmtSize(rec.totalSizeBytes ?? undefined)}
                  </MutedCell>
                  <td
                    title={
                      status.key === 'failed'
                        ? rec.failureReason || undefined
                        : undefined
                    }
                  >
                    <L.Tag tone={status.tone}>
                      {t(`pages.groupDetail.zoom.status.${status.key}`)}
                    </L.Tag>
                  </td>
                  <MutedCell>{rec.zoomAccount?.displayName || '—'}</MutedCell>
                </tr>
              );
            })}
          </tbody>
        </L.SmallTable>
      </S.Card>
    );
  },
);
